import { cn } from '@/lib/utils';
import ferdyBody from '@/assets/ferdy/ferdy-body.webp';
import ferdyMop from '@/assets/ferdy/ferdy-mop.webp';

// Suds that rise off the mop head (positions are % of the stage)
const bubbles = [
  { left: 6, bottom: 4, size: 14, delay: 0, bx: -18 },
  { left: 16, bottom: 2, size: 9, delay: 0.7, bx: 10 },
  { left: 26, bottom: 5, size: 18, delay: 1.4, bx: -8 },
  { left: 34, bottom: 3, size: 11, delay: 2.1, bx: 16 },
  { left: 11, bottom: 6, size: 7, delay: 2.6, bx: 6 },
  { left: 22, bottom: 1, size: 12, delay: 1.1, bx: -14 },
];

const sparkles = [
  { left: 2, bottom: 12, size: 16, delay: 0.3 },
  { left: 40, bottom: 9, size: 12, delay: 1.2 },
  { left: 18, bottom: 16, size: 10, delay: 1.9 },
];

/** Comic-style puddle under Ferdy: flat blue, bold outline, white glints. */
function Puddle() {
  return (
    <svg className="ferdy-puddle" viewBox="0 0 200 56" aria-hidden focusable="false">
      <g className="ferdy-puddle-body">
        <path
          d="M14 30C5 22 21 12 40 16C52 7 75 9 87 15C101 6 129 8 141 16C159 10 187 15 189 25C197 34 181 42 164 41C150 48 124 47 110 43C94 50 66 49 54 43C35 48 11 42 14 30Z"
          fill="#8fd6ff"
          stroke="#1c3f8f"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <path
          d="M34 26C30 21 44 17 56 20C66 15 84 17 92 21C104 16 126 17 134 22C148 19 168 22 168 28C170 33 156 35 144 33C130 37 108 36 98 33C84 37 62 36 52 33C40 35 31 31 34 26Z"
          fill="#c8eeff"
        />
        <path d="M44 22C52 19 62 19 70 21" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        <path d="M142 23C148 21 155 21 160 23" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        <circle cx="78" cy="22" r="2" fill="#fff" />
      </g>
      {/* Stray drops round the edge */}
      <ellipse cx="6" cy="40" rx="4.5" ry="3" fill="#8fd6ff" stroke="#1c3f8f" strokeWidth="2" />
      <ellipse cx="194" cy="38" rx="3.5" ry="2.4" fill="#8fd6ff" stroke="#1c3f8f" strokeWidth="2" />
      {/* Ripple where the mop head wipes */}
      <ellipse className="ferdy-ripple" cx="62" cy="30" rx="16" ry="5" fill="none" stroke="#fff" strokeWidth="2" />
      {/* Splash drops flicked up by the mop */}
      <g className="ferdy-splash">
        <path d="M50 22c-3-5 1-9 3-10 1 3 2 7-3 10Z" fill="#8fd6ff" stroke="#1c3f8f" strokeWidth="1.6" />
        <path d="M62 18c-1-6 3-8 5-9 0 3 0 7-5 9Z" fill="#8fd6ff" stroke="#1c3f8f" strokeWidth="1.6" />
        <path d="M74 21c0-5 4-7 6-7-1 3-2 6-6 7Z" fill="#8fd6ff" stroke="#1c3f8f" strokeWidth="1.6" />
      </g>
    </svg>
  );
}

/**
 * Ferdy, our cleaning mascot, mopping a comic puddle on a loop. The mop is a
 * separate layer that swings from his hand while his body sways in time.
 */
export function FerdyMopping({ className }: { className?: string }) {
  return (
    <div className={cn('ferdy-stage', className)} role="img" aria-label="Ferdy, The Polished Version mascot, mopping a puddle">
      <Puddle />
      <div className="ferdy-rig" aria-hidden>
        <img src={ferdyBody} alt="" width={512} height={768} draggable={false} />
        <img src={ferdyMop} alt="" width={512} height={768} draggable={false} className="ferdy-mop" />
      </div>
      {bubbles.map((b, i) => (
        <span
          key={`b${i}`}
          aria-hidden
          className="ferdy-bubble"
          style={{
            left: `${b.left}%`,
            bottom: `${b.bottom}%`,
            width: `${b.size / 3.8}%`,
            aspectRatio: '1 / 1',
            animationDelay: `${b.delay}s`,
            ['--bx' as string]: `${b.bx * 7}%`,
          }}
        />
      ))}
      {sparkles.map((s, i) => (
        <span
          key={`s${i}`}
          aria-hidden
          className="ferdy-sparkle"
          style={{ left: `${s.left}%`, bottom: `${s.bottom}%`, width: `${s.size / 3.8}%`, aspectRatio: '1 / 1', animationDelay: `${s.delay}s` }}
        />
      ))}
    </div>
  );
}
